// Tiny allowlist HTML sanitizer for RSS article bodies. Built for the
// vetted feeds we ingest (apps/api/src/scrapers/articles/*.ts) — strong
// enough to neuter the obvious bad stuff (scripts, event handlers,
// javascript: URLs) without pulling in DOMPurify (~30 KB).
//
// WHY this lives client-side: the API stores the raw <content:encoded>
// from RSS verbatim. Sanitizing at render keeps the original around so
// the rule set can evolve without re-scraping every article.
//
// IF we ever accept user-submitted HTML (e.g. comments, posts):
// switch to DOMPurify. Don't extend this. The risk model is different.

const ALLOWED_TAGS = new Set([
  "p", "br", "strong", "em", "b", "i", "u", "s",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li", "blockquote", "pre", "code",
  "a", "img", "figure", "figcaption",
  "table", "thead", "tbody", "tr", "td", "th",
  "div", "span", "hr",
]);

// Tags whose contents should be DROPPED, not just unwrapped.
const DROP_WITH_CONTENT = new Set([
  "script", "style", "iframe", "object", "embed", "noscript",
  "form", "input", "button", "textarea", "select",
  "video", "audio", "canvas", "svg", "math",
]);

interface AttrSpec {
  allow: Set<string>;
  /** Optional per-attribute URL guard. */
  urlAttrs?: Set<string>;
  /** Force-add these key=value pairs. */
  force?: Record<string, string>;
}

const ATTRS_BY_TAG: Record<string, AttrSpec> = {
  a:   { allow: new Set(["href", "title"]), urlAttrs: new Set(["href"]),
         force: { target: "_blank", rel: "noopener noreferrer" } },
  img: { allow: new Set(["src", "alt", "width", "height", "title"]),
         urlAttrs: new Set(["src"]) },
  // The structural / inline tags get no attrs by default — we strip
  // class/id/style across the board so the article inherits app styling.
};
const NO_ATTRS: AttrSpec = { allow: new Set() };

const SAFE_URL_RE = /^(?:https?:\/\/|\/|mailto:|#)/i;
function isSafeUrl(u: string): boolean {
  return SAFE_URL_RE.test(u.trim());
}

/**
 * Returns a sanitized HTML string. Run the result through React via
 * `dangerouslySetInnerHTML={{ __html: sanitize(html) }}`.
 *
 * Strategy: walk the HTML as a regex token stream. We can't trust the
 * browser's HTMLParser (server-side rendering doesn't have one) or
 * DOMParser (Worker doesn't). This is intentionally conservative:
 * unknown tags get unwrapped (kept text, dropped wrapper).
 */
export function sanitizeHtml(input: string | null | undefined): string {
  if (!input) return "";

  // First pass: drop entire blocks for the must-die tags. Greedy by
  // design — if a feed has nested <script>s, the outer match wins and
  // both go.
  let html = input;
  for (const tag of DROP_WITH_CONTENT) {
    const re = new RegExp(`<${tag}\\b[\\s\\S]*?<\\/${tag}\\s*>`, "gi");
    html = html.replace(re, "");
    // Self-closing / never-closed variants.
    const reSelf = new RegExp(`<${tag}\\b[^>]*\\/?>`, "gi");
    html = html.replace(reSelf, "");
  }

  // Second pass: rewrite every remaining tag. Text content between tags
  // is left untouched (already HTML-escaped by the publisher).
  return html.replace(/<\/?([a-zA-Z][a-zA-Z0-9-]*)\b([^>]*)>/g, (match, rawName: string, rawAttrs: string) => {
    const name = rawName.toLowerCase();
    const isClose = match.startsWith("</");

    if (!ALLOWED_TAGS.has(name)) {
      // Unknown tag → drop the wrapper, keep any text inside (because we
      // matched only the tag itself, not its contents).
      return "";
    }
    if (isClose) return `</${name}>`;

    const spec = ATTRS_BY_TAG[name] ?? NO_ATTRS;
    const kept: string[] = [];
    // Parse attrs cheaply. Handles key="v", key='v', and bare keys.
    const attrRe = /([a-zA-Z_:][\w:.-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g;
    let m: RegExpExecArray | null;
    while ((m = attrRe.exec(rawAttrs)) !== null) {
      const k = m[1]!.toLowerCase();
      // Strip on*, style, class, id everywhere. Even on allowed tags.
      if (k.startsWith("on") || k === "style" || k === "class" || k === "id") continue;
      if (!spec.allow.has(k)) continue;
      const v = m[2] ?? m[3] ?? m[4] ?? "";
      if (spec.urlAttrs?.has(k) && !isSafeUrl(v)) continue;
      // Quote values consistently and escape any embedded quote.
      kept.push(`${k}="${v.replace(/"/g, "&quot;")}"`);
    }
    if (spec.force) {
      for (const [k, v] of Object.entries(spec.force)) kept.push(`${k}="${v}"`);
    }
    const attrs = kept.length ? " " + kept.join(" ") : "";
    // void/self-close img + hr + br
    if (name === "img" || name === "hr" || name === "br") return `<${name}${attrs}>`;
    return `<${name}${attrs}>`;
  });
}
